import { supabase } from "@/integrations/supabase/client";
import {
  isDuplicateProposeError,
  isVoiceRateLimitError,
  VOICE_WRITE_ALREADY_SAVED_RESULT,
  VOICE_WRITE_RATE_LIMITED_RESULT,
} from "@/lib/voiceWriteBudget";

/** What the write did. `duplicate` means this proposal had already been saved. */
export type CaptureReminderAction = "insert" | "duplicate" | "rejected_rate_limit";

export interface CaptureReminderResult {
  ok: boolean;
  title?: string;
  action?: CaptureReminderAction;
  /** Set only when ok is false. Already phrased for Kin to speak. */
  error?: string;
  /**
   * Set only when ok is TRUE and there is something the model should know
   * anyway — today, that this proposal had already been saved. Kept separate
   * from `error` because this object is handed to the model as the tool result,
   * and a success carrying an `error` field reads as a failure.
   */
  note?: string;
}

/**
 * Reuses brain-dump-parse's exact insert shape (task_queue_batches row +
 * one task_queue_items row, action_type "reminder", status "draft") so a
 * captured card renders through the same component with the same
 * Done/Skip/Undo as a Brain Dump card. One executor; Voice Capture is its
 * first caller, Phase 2 text capture (kin-chat tag) is the planned second.
 *
 * `source` is required, not defaulted, on purpose (#684) — it's the only
 * thing that lets the Tasks tab show her where a card came from, and a
 * default would silently mislabel the next caller that forgets to pass one.
 * As of #933 it is also what scopes the per-window write cap: only rows marked
 * `voice` are budgeted, so a ten-item brain dump is not refused.
 *
 * ── proposeId (#933) ────────────────────────────────────────────────────────
 *
 * Optional. When present it is recorded on the row under a unique index, so
 * calling this twice for the same proposal writes ONE card, not two.
 *
 * ⚠️ The client-side gate already resets after every capture attempt, so it
 * catches the model calling capture_reminder twice. What it does NOT catch —
 * and what this is for — is THIS FUNCTION being entered twice for a single tool
 * call: a retry after a request that actually landed, a handler that fires on
 * two events, a state machine that fails to reset. No adversary required.
 */
export async function captureReminder(
  userId: string,
  text: string,
  source: string,
  proposeId?: string | null
): Promise<CaptureReminderResult> {
  const body = text.trim();
  const cleaned = body.replace(/[\s.,!?;:]+$/, "").trim();
  if (!cleaned) return { ok: false, error: "Nothing to save" };

  const words = cleaned.split(/\s+/).filter(Boolean);
  const title = words.length > 6 ? words.slice(0, 6).join(" ") : cleaned;

  // ── The RPC path (#933) ────────────────────────────────────────────────────
  //
  // Both inserts in one transaction, so a refused item cannot leave an orphan
  // batch behind. See the migration's STEP 5 for why that matters: this table
  // family has NO client DELETE policy, so the legacy cleanup below removes
  // zero rows and returns 200 with error === null — it cannot even report that
  // it failed.
  const { data: rpcData, error: rpcErr } = await (supabase as any).rpc(
    "capture_voice_reminder",
    {
      p_title: title,
      p_body: body,
      p_source: source,
      ...(proposeId ? { p_propose_id: proposeId } : {}),
    },
  );

  // PGRST202 = the function does not exist yet. The frontend ships on merge and
  // Kasandra runs the SQL by hand, so this window is real. Everything else is a
  // genuine failure and must NOT silently fall back to the path with the orphan
  // problem.
  const migrationMissing = String((rpcErr as any)?.code ?? "") === "PGRST202";

  if (!migrationMissing) {
    if (rpcErr) {
      // ⚠️ The race the unique index exists for. Two truly simultaneous calls
      // can both pass the propose lookup inside the function; one of them then
      // loses on the index and the whole transaction aborts. That is a
      // SUCCESS — the other call wrote the card — and must not be reported as
      // a failure the model will retry against.
      if (isDuplicateProposeError(rpcErr)) {
        return { ok: true, title, action: "duplicate", note: VOICE_WRITE_ALREADY_SAVED_RESULT };
      }
      if (isVoiceRateLimitError(rpcErr)) {
        return { ok: false, action: "rejected_rate_limit", error: VOICE_WRITE_RATE_LIMITED_RESULT };
      }
      console.error("[captureReminder] rpc capture_voice_reminder failed:", rpcErr);
      return { ok: false, error: "Couldn't save that" };
    }

    const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
    const action = row?.action as string | undefined;

    // A call that resolved without error is not evidence a row was written.
    // #537's fix created #545 exactly by assuming the happy path here.
    if (!action) {
      console.error("[captureReminder] rpc returned no action row:", rpcData);
      return { ok: false, error: "Couldn't save that" };
    }
    if (action === "duplicate") {
      return { ok: true, title, action: "duplicate", note: VOICE_WRITE_ALREADY_SAVED_RESULT };
    }
    if (action === "rejected_rate_limit") {
      return { ok: false, action: "rejected_rate_limit", error: VOICE_WRITE_RATE_LIMITED_RESULT };
    }
    if (action !== "insert") {
      return { ok: false, error: "Couldn't save that" };
    }
    return { ok: true, title, action: "insert" };
  }

  // ── Legacy two-insert path — ONLY until the migration runs ─────────────────
  //
  // Byte-for-byte what shipped before #933, because that is what the database
  // supports in this window: no propose_id column, no cap, no RPC. It carries
  // the orphan-batch flaw described above, which is exactly why it is now
  // unreachable the moment Kasandra runs the SQL.
  const { data: batchRow, error: batchErr } = await supabase
    .from("task_queue_batches")
    .insert({ user_id: userId, raw_input: body, status: "draft", item_count: 1 })
    .select("id")
    .single();

  if (batchErr || !batchRow) {
    console.error("[captureReminder] batch insert failed:", batchErr);
    return { ok: false, error: "Couldn't save that" };
  }

  // Cast to `any` on this one call: `source` is a real column as of #684's
  // migration, but src/integrations/supabase/types.ts is regenerated from
  // the LIVE schema and can't know about it until Kasandra runs that
  // migration — same generated-types-lag every new-column PR in this repo
  // hits. Remove the cast once types.ts is regenerated post-merge.
  const { error: itemErr } = await (supabase as any)
    .from("task_queue_items")
    .insert({
      batch_id: batchRow.id,
      user_id: userId,
      action_type: "reminder",
      title,
      body,
      send_method: "manual",
      status: "draft",
      sort_order: 0,
      source,
      // No propose_id here on purpose: this path only runs when the migration
      // has NOT been applied, so the column does not exist.
    });

  if (itemErr) {
    console.error("[captureReminder] item insert failed:", itemErr);

    // ⚠️ This cleanup probably removes nothing, and that is now recorded rather
    // than assumed. `.select("id")` is what makes the difference visible: an
    // RLS-filtered DELETE with no matching policy returns 200 with
    // error === null and zero rows (house rule 16), so reading only `error`
    // cannot see the failure. No client DELETE policy for this table family is
    // recorded anywhere in the repo — see e2e/resetFixtureAccount.ts.
    //
    // Left as a log rather than a fix because the fix is the RPC above. This
    // path stops running the moment the SQL is applied.
    const { data: cleaned, error: cleanupErr } = await supabase
      .from("task_queue_batches")
      .delete()
      .eq("id", batchRow.id)
      .select("id");
    if (cleanupErr) {
      console.error("[captureReminder] cleanup delete failed:", cleanupErr);
    } else if (!Array.isArray(cleaned) || cleaned.length === 0) {
      console.error(
        "[captureReminder] cleanup delete removed NO rows — orphan batch left behind:",
        batchRow.id,
      );
    }

    return { ok: false, error: "Couldn't save that" };
  }

  return { ok: true, title, action: "insert" };
}
